import { useCallback, useEffect, useState } from 'react';
import { api, type PushDevice } from '../lib/api';
import {
  describeDevice,
  explainBlocker,
  getBrowserAdvice,
  getPlatformInfo,
  type PlatformInfo,
} from '../lib/platform';
import { disablePush, enablePush, getExistingSubscription } from '../lib/pushClient';
import { BellIcon, ShareIcon } from './Icons';

/**
 * Notification setup and diagnostics.
 *
 * The design rule here: never show a control that might not work. If push is
 * unavailable, say which precondition is missing and how to fix that specific
 * thing. And once it is on, show real per-device delivery state — last
 * successful send, last error — plus a test button that goes through the
 * genuine pipeline, so "are notifications working on Mum's phone?" is a
 * question with an answer instead of a guess.
 */
export function NotificationSetup({ compact = false }: { compact?: boolean }) {
  const [platform, setPlatform] = useState<PlatformInfo>(() => getPlatformInfo());
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [devices, setDevices] = useState<PushDevice[]>([]);
  const [busy, setBusy] = useState(false);
  const [showCompactRecovery, setShowCompactRecovery] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const refresh = useCallback(async () => {
    setPlatform(getPlatformInfo());

    const subscription = await getExistingSubscription();
    setSubscribed(subscription !== null);

    try {
      const status = await api.pushStatus();
      setDevices(status.devices);
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Re-check when the app regains focus. On iOS the user leaves to add the app
   * to the Home Screen, or to flip a switch in Settings, and comes back — the
   * screen should reflect that without a manual reload.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const explanation = explainBlocker(platform);
  const browserAdvice = getBrowserAdvice(platform);

  const handleEnable = async () => {
    setBusy(true);
    setFeedback(null);

    const result = await enablePush();

    if (result.ok) {
      setFeedback({
        kind: 'ok',
        text: 'Notifications are on. Send yourself a test to be sure.',
      });
    } else {
      setFeedback({
        kind: 'error',
        text: result.message ?? 'Could not turn notifications on.',
      });
    }

    await refresh();
    setBusy(false);
  };

  const handleDisable = async () => {
    setBusy(true);
    setFeedback(null);
    await disablePush();
    await refresh();
    setFeedback({ kind: 'ok', text: 'Notifications are off for this device.' });
    setBusy(false);
  };

  const handleTest = async (subscriptionId?: string) => {
    setBusy(true);
    setFeedback(null);

    try {
      const result = await api.testPush(subscriptionId);
      const delivered = result.results.filter((entry) => entry.ok).length;
      const failed = result.results.filter((entry) => !entry.ok);

      if (delivered > 0 && failed.length === 0) {
        setFeedback({
          kind: 'ok',
          text: 'Test sent. It should appear within a few seconds — if it does not, the device may have notifications muted in system settings.',
        });
      } else if (delivered > 0) {
        setFeedback({
          kind: 'ok',
          text: `Sent to ${delivered} device(s); ${failed.length} failed. See the list below.`,
        });
      } else {
        setFeedback({
          kind: 'error',
          text: failed[0]?.error ?? 'The test could not be delivered.',
        });
      }
    } catch (error) {
      setFeedback({
        kind: 'error',
        text: error instanceof Error ? error.message : 'The test failed to send.',
      });
    }

    await refresh();
    setBusy(false);
  };

  // Keep feed recovery discoverable without pushing the family photos below
  // a full troubleshooting card. The complete diagnostics remain in Settings.
  if (compact && subscribed === false && platform.blocker !== 'none') {
    return (
      <div className="notification-compact banner attention">
        <button
          type="button"
          className="notification-compact-toggle"
          aria-expanded={showCompactRecovery}
          onClick={() => setShowCompactRecovery((current) => !current)}
        >
          <BellIcon className="" />
          <span>
            <strong>{explanation.title}</strong>
            <small>{showCompactRecovery ? 'Hide help' : 'How to fix'}</small>
          </span>
        </button>
        {showCompactRecovery && <div className="notification-compact-detail">
          <p>{explanation.detail}</p>
          {explanation.steps && (
            <ol className="steps">
              {explanation.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </div>}
      </div>
    );
  }

  // --- the iOS install coach ------------------------------------------------
  if (platform.blocker === 'ios-needs-install') {
    return (
      <div className="banner attention">
        <h3>
          <ShareIcon
            className=""
            // Inline sizing keeps the icon aligned with the heading text.
          />{' '}
          {explanation.title}
        </h3>
        <p>{explanation.detail}</p>
        {explanation.steps && (
          <ol className="steps">
            {explanation.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        )}
      </div>
    );
  }

  if (!explanation.actionable && platform.blocker !== 'none') {
    return (
      <div className="banner attention">
        <h3>{explanation.title}</h3>
        <p>{explanation.detail}</p>
        {explanation.steps && (
          <ol className="steps">
            {explanation.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        )}
      </div>
    );
  }

  // --- compact prompt, shown on the feed -----------------------------------
  if (compact) {
    if (subscribed !== false) return null;

    return (
      <div className="banner attention">
        <h3>Turn on notifications</h3>
        <p>Get a nudge when someone shares a new photo or sends a message.</p>
        <button
          type="button"
          className="btn btn-primary btn-block"
          style={{ marginTop: 12 }}
          onClick={() => void handleEnable()}
          disabled={busy}
        >
          {busy ? 'Setting up…' : 'Turn on notifications'}
        </button>
      </div>
    );
  }

  // --- full settings panel -------------------------------------------------
  return (
    <div className="card">
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <BellIcon className="" />
        Notifications
      </h2>

      {/*
        Surfaced even when push is technically available: on Firefox for
        Android it usually subscribes successfully and then never delivers,
        so the honest place to say so is next to the controls.
      */}
      {browserAdvice && (
        <div className="banner attention" style={{ marginTop: 4, marginBottom: 12 }}>
          <h3>{browserAdvice.title}</h3>
          <p>{browserAdvice.detail}</p>
          {browserAdvice.steps && (
            <ol className="steps">
              {browserAdvice.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </div>
      )}

      {subscribed === null ? (
        <p>Checking…</p>
      ) : subscribed ? (
        <p>This device is set up to receive notifications.</p>
      ) : (
        <p>This device will not receive notifications yet.</p>
      )}

      {feedback && (
        <div
          className={`banner ${feedback.kind === 'error' ? 'error' : ''}`}
          style={{ marginTop: 12, marginBottom: 12 }}
        >
          <p>{feedback.text}</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
        {subscribed ? (
          <>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => void handleTest()}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send me a test notification'}
            </button>
            <button
              type="button"
              className="btn btn-danger btn-block"
              onClick={() => void handleDisable()}
              disabled={busy}
            >
              Turn off on this device
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => void handleEnable()}
            disabled={busy}
          >
            {busy ? 'Setting up…' : 'Turn on notifications'}
          </button>
        )}
      </div>

      {devices.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: '0.78rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--text-faint)',
              fontWeight: 650,
              marginBottom: 4,
            }}
          >
            Your devices
          </div>

          {devices.map((device) => (
            <div className="device-row" key={device.id}>
              <span
                className={`status-dot ${
                  device.failureCount > 0 ? 'bad' : device.lastSuccessAt ? 'ok' : 'warn'
                }`}
                aria-hidden="true"
              />
              <div className="device-meta">
                <div className="device-name">{describeDevice(device.userAgent)}</div>
                <div className="device-detail">
                  {device.lastError
                    ? device.lastError
                    : device.lastSuccessAt
                      ? `Last delivered ${new Date(device.lastSuccessAt).toLocaleString(undefined, {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}`
                      : 'Nothing delivered yet'}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void handleTest(device.id)}
                disabled={busy}
              >
                Test
              </button>
            </div>
          ))}
        </div>
      )}

      <p style={{ marginTop: 16, fontSize: '0.82rem', color: 'var(--text-faint)' }}>
        Notifications are set up separately on every phone, tablet and computer you use.
      </p>
    </div>
  );
}
